import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@amzn/meridian/box';
import { ROUTES } from '../../../utils/constants';
import AppModal from '../../../components/AppModal/AppModal';
import RatesRosterLoader from '../../../components/RatesRosterLoader/RatesRosterLoader';
import { useAppDispatch } from '../../../store';
import {
  setHistoricalPlanData,
  setStationAssignmentData,
} from '../../../store/slices/stationAssignmentSlice';
import { getClockWindowNameBasedOnSelectedOption } from '../../../services/shiftPlanService';
import { getStationAssignments } from '../assignments/api';
import PublishedAssignmentHeader from './PublishedAssignmentHeader';
import PublishedAssignmentScreen, {
  PublishedAssignmentView,
} from './PublishedAssignmentScreen';
import './index.scss';

/**
 * Smart container for the Published Assignment Screen.
 *
 * Owns routing parameter extraction, the data-fetch effect, and the four
 * discrete render states (loading / missing-plan-id / generic-error /
 * success). Mirrors the inline-state pattern used by the sibling editable
 * Assignments page (`assignments/index.tsx`) and the Associate View page
 * (`associateView/index.tsx`):
 *
 *   - State is held in plain `useState` hooks (`response`, `loading`,
 *     `errorKind`, `searchQuery`) declared inline at the top of the
 *     component.
 *   - The `errorKind` field uses a small inline union literal — declared
 *     locally, not exported, no separate `state.ts` module.
 *   - The three non-content state `data-testid`s are inline string
 *     literals at the render site (no module-level testid constants),
 *     matching the sibling-page convention.
 *
 * The render ladder is mutually exclusive:
 *
 *   loading        → `RatesRosterLoader` (full-screen progress UI), wrapped
 *                    in a div with `data-testid="published-assignment-loading"`.
 *   missing-plan   → `AppModal` (error variant) whose primary action
 *                    navigates to `ROUTES.STATION_ASSIGNMENT`, wrapped in
 *                    a div with
 *                    `data-testid="published-assignment-missing-plan-id"`.
 *   generic-error  → `AppModal` (error variant) whose primary action takes
 *                    the user back to `ROUTES.STATION_ASSIGNMENT`, wrapped
 *                    in a div with
 *                    `data-testid="published-assignment-generic-error"`.
 *   success        → `<PublishedAssignmentHeader />` followed by
 *                    `<PublishedAssignmentScreen ... />`.
 *
 * URL-parameter validation happens before the API call: an `undefined`,
 * empty, or whitespace-only `:planId` short-circuits to the missing-plan
 * state without invoking `getStationAssignments`. Otherwise the
 * effect awaits `getStationAssignments(ofdDate, clockWindowName, planId)`
 * and uses the same structural-emptiness check as the wrapper's failure
 * surface (`associates.length === 0 && processPaths.length === 0`) to
 * decide whether to render success or the generic-error state. The wrapper
 * has already dispatched `showError` on a real failure, so the container
 * does not need to fire its own toast.
 *
 * The container determines the Plan_Id solely from the URL (it does not
 * read `isStationAssignmentActive` from Redux), so direct URL access works
 * for isolated development before the History screen is built.
 */
const PublishedAssignmentContainer: React.FC = () => {
  const { planId } = useParams<{ planId?: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Hardcoded values for now — will be replaced with query param extraction later.
  const ofdDate = '2026-07-02';
  const shift = 'day';
  const clockWindowName = getClockWindowNameBasedOnSelectedOption(shift);

  const [response, setResponse] = useState<PublishedAssignmentView | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [errorKind, setErrorKind] = useState<
    { kind: 'generic' | 'missing-plan-id'; message?: string } | null
  >(null);
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    // URL-parameter validation: an `undefined`, empty, or whitespace-only
    // `:planId` short-circuits without invoking the API. Use a synchronous
    // path so we never enter the loading state for a request we won't make.
    if (!planId || planId.trim() === '') {
      setLoading(false);
      setErrorKind({ kind: 'missing-plan-id' });
      setResponse(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setErrorKind(null);

    const run = async () => {
      try {
        const result = await getStationAssignments(
          ofdDate,
          clockWindowName,
          planId,
        );
        if (cancelled) return;

        // Flatten the published plan to the first half of the shift. The
        // published screen is a read-only snapshot, so it renders the first
        // half by default (mirroring the editable page's
        // `DEFAULT_SHIFT_HALF_OPTIONS[0]`) without a half selector.
        const activeHalf = result?.firstHalfStationAssignment;

        // Structural emptiness check: the wrapper already dispatched
        // `showError` for any real failure (returning `EMPTY_RESPONSE`),
        // and Req 16 maps an empty-but-successful response to the generic
        // error state too. Both cases are caught here.
        if (
          !result ||
          !activeHalf ||
          (activeHalf.associates.length === 0 &&
            activeHalf.processPaths.length === 0)
        ) {
          setErrorKind({ kind: 'generic' });
          setResponse(null);
        } else {
          setResponse({
            associates: activeHalf.associates,
            processPaths: activeHalf.processPaths,
            metrics: activeHalf.metrics,
            lastModifiedBy: result.lastModifiedBy,
            lastModifiedDate: result.lastModifiedDate,
          });
          setErrorKind(null);

          // Store the full response in Redux so Associate View can render
          // directly without making another API call.
          dispatch(setHistoricalPlanData(result));
          dispatch(setStationAssignmentData({ date: ofdDate, shift }));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [planId, ofdDate, clockWindowName]);

  const handleErrorAcknowledge = () => {
    navigate(ROUTES.SHIFT_ROTATION);
  };

  // ── Render ladder ───────────────────────────────────────────────────────

  if (loading) {
    return (
      <div data-testid="published-assignment-loading">
        <RatesRosterLoader
          type="plan"
          status="loading"
          message="Loading published station assignment plan..."
        />
      </div>
    );
  }

  if (errorKind?.kind === 'missing-plan-id') {
    return (
      <div data-testid="published-assignment-missing-plan-id">
        <AppModal
          open
          title="Unable to load published assignment"
          message="A Plan_Id is required to view a published station assignment."
          variant="error"
          primaryLabel="OK"
          onPrimary={handleErrorAcknowledge}
          onClose={handleErrorAcknowledge}
        />
      </div>
    );
  }

  if (errorKind?.kind === 'generic' || !response) {
    return (
      <div data-testid="published-assignment-generic-error">
        <AppModal
          open
          title="Unable to load published assignment"
          message={
            errorKind?.message ??
            'Unable to fetch the published assignment plan details. Please try again later.'
          }
          variant="error"
          primaryLabel="OK"
          onPrimary={handleErrorAcknowledge}
          onClose={handleErrorAcknowledge}
        />
      </div>
    );
  }

  // Success branch. The header reuses `lastModifiedBy` / `lastModifiedDate`
  // as submitted-by / submitted-date (see design.md → "Plan-metadata fields
  // in the header"). The screen does not currently know the OFD-date or
  // clock-window for a published plan — the wrapper sends `''` for both —
  // so we pass empty strings here; a future iteration can surface them
  // from a richer response without changing this contract.
  return (
    <Box className="published-assignment-page">
      <PublishedAssignmentHeader
        planId={planId ?? ''}
        submittedBy={response.lastModifiedBy}
        submittedDate={response.lastModifiedDate}
        ofdDate={ofdDate}
        clockWindow={clockWindowName}
      />
      <PublishedAssignmentScreen
        response={response}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        planId={planId ?? ''}
      />
    </Box>
  );
};

export default PublishedAssignmentContainer;

import './index.scss';